/**
 * gate-board.ts — the availability gates as ONE board: every zone, both gates, and what is
 * EFFECTIVELY open (G1/G2, Jeff, 4–5 October 2026).
 *
 * ⭐⭐ WHY THIS EXISTS (G2 — the live consequence). On 4 October Jeff opened the PLATFORM sign-up
 * switch to test US sign-up. Australia's own switch was already open underneath it, so
 * Australia's public sign-up opened too, which nobody intended (read live 5 October:
 * `/gate/state?zone=au` → open). The page showed each switch on its own; nothing showed what the
 * PUBLIC would actually meet. This module computes exactly that, the way the edge does.
 *
 * ⛔ THE SEMANTICS ARE THE EDGE'S, NOT REINVENTED (ADR-124 D6/I8). `workers/sow-gate/src/gate.ts`
 * `effectiveOpen(global, zone) = isOpen(global) && isOpen(zone)`: a request passes only when the
 * platform kill-switch AND its zone's own switch are open. Anything not read as open is closed —
 * a failed read included — so this can only ever UNDER-state what is open, never over-state it.
 */
import type { SowGateState } from './nexa-config-api';

export type GateKind = 'sow' | 'beta';

export const GATE_LABEL: Record<GateKind, string> = {
  sow: 'Public sign-up',
  beta: 'Beta invites',
};

/** A read that failed is not a state — it is shown as such and counted as CLOSED. */
export type GateRead = SowGateState | 'error' | null;

export const isOpenRead = (r: GateRead): boolean => r !== null && r !== 'error' && r.mode === 'open';

/** What the public meets in a zone: the platform switch AND the zone's own switch. */
export function effectiveOpen(platform: GateRead, zone: GateRead): boolean {
  return isOpenRead(platform) && isOpenRead(zone);
}

export interface ZoneGateRow {
  zone: string;
  /** The zone's jurisdiction, as an operator and a customer both say it ("Australia"). */
  jurisdiction: string;
  gate: GateKind;
  own: GateRead;
}

/**
 * G2 — the zones whose EFFECTIVE state flips if the platform switch for `gate` moves to `target`.
 * Opening it opens every zone whose own switch is already open; closing it closes every zone that
 * is effectively open now. The platform switch's confirmation lists these and must be confirmed:
 * "opening the platform switch must never silently open a zone".
 */
export function zonesChangedByPlatform(
  rows: readonly ZoneGateRow[],
  gate: GateKind,
  platformNow: GateRead,
  target: 'open' | 'closed',
): Array<{ zone: string; jurisdiction: string; to: 'open' | 'closed' }> {
  const nowOpen = isOpenRead(platformNow);
  const nextOpen = target === 'open';
  return rows
    .filter((r) => r.gate === gate)
    .filter((r) => effectiveOpen(nowOpen ? { mode: 'open' } : { mode: 'closed' }, r.own)
      !== effectiveOpen(nextOpen ? { mode: 'open' } : { mode: 'closed' }, r.own))
    .map((r) => ({ zone: r.zone, jurisdiction: r.jurisdiction, to: target }));
}

/** The sentence every zone toggle confirms (G1): "Public sign-up in Australia will be OPEN …". */
export function zoneToggleOutcome(row: ZoneGateRow, platform: GateRead, target: 'open' | 'closed'): {
  effective: 'open' | 'closed';
  sentence: string;
} {
  const effective = effectiveOpen(platform, { mode: target }) ? 'open' : 'closed';
  const who = row.gate === 'sow' ? 'to the public' : 'for beta invites';
  const sentence = effective === 'open'
    ? `${GATE_LABEL[row.gate]} in ${row.jurisdiction} will be OPEN ${who}.`
    : target === 'open'
      ? `${GATE_LABEL[row.gate]} in ${row.jurisdiction} will stay CLOSED — the platform emergency stop is closed. This zone will open when the emergency stop opens.`
      : `${GATE_LABEL[row.gate]} in ${row.jurisdiction} will be CLOSED.`;
  return { effective, sentence };
}
